import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

export default function useNotifications({
  userId,
  tab,
  notifications,
  notificationsHasMore,
  notificationsOlderLoading,
  notificationBusyId,
  notificationBulkBusy,
  unreadNotificationCount,
  setNotifications,
  setUnreadNotificationCount,
  setNotificationsHasMore,
  setNotificationsLoading,
  setNotificationsOlderLoading,
  setNotificationsError,
  setNotificationsOlderError,
  setNotificationBusyId,
  setNotificationBulkBusy,
}) {
  const activeTab = useRef(tab);
  const loadedFor = useRef('');
  const loadNotificationsRef = useRef(null);
  activeTab.current = tab;

  const loadNotifications = useCallback(async (showLoading = true, appendOlder = false) => {
    if (!supabase || !userId) {
      setNotifications([]);
      setUnreadNotificationCount(0);
      setNotificationsHasMore(false);
      setNotificationsLoading(false);
      setNotificationsOlderLoading(false);
      return;
    }
    if (appendOlder) {
      if (notificationsOlderLoading || !notificationsHasMore) return;
      setNotificationsOlderLoading(true);
      setNotificationsOlderError('');
    } else if (showLoading) {
      setNotificationsLoading(true);
    }
    setNotificationsError('');
    try {
      const cursor = appendOlder ? notifications[notifications.length - 1] : null;
      let listQuery = supabase.from('notifications').select('*')
        .eq('recipient_id', userId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });
      if (cursor) {
        listQuery = listQuery.or(
          `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
        );
      }
      const [listResult, countResult] = await Promise.all([
        listQuery.limit(51),
        supabase.from('notifications').select('id', { count: 'exact', head: true })
          .eq('recipient_id', userId).is('read_at', null),
      ]);
      const errors = [];
      if (listResult.error) {
        if (appendOlder) setNotificationsOlderError(`Could not load older notifications: ${listResult.error.message}`);
        else errors.push(`Could not load notifications: ${listResult.error.message}`);
      } else {
        const rows = listResult.data || [];
        const page = rows.slice(0, 50);
        setNotifications(current => {
          const merged = new Map((appendOlder
            ? current
            : page.concat(current.filter(item => !page.some(row => row.id === item.id)))
          ).map(item => [item.id, item]));
          for (const item of page) merged.set(item.id, item);
          return [...merged.values()].sort(
            (a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id),
          );
        });
        setNotificationsHasMore(rows.length > 50 || (!appendOlder && notificationsHasMore));
      }
      if (countResult.error) errors.push(`Could not load unread notification count: ${countResult.error.message}`);
      else setUnreadNotificationCount(countResult.count || 0);
      setNotificationsError(errors.join(' '));
    } catch (error) {
      const message = error.message || 'Network request failed.';
      if (appendOlder) setNotificationsOlderError(`Could not load older notifications: ${message}`);
      else setNotificationsError(`Could not load notifications: ${message}`);
    } finally {
      if (appendOlder) setNotificationsOlderLoading(false);
      else if (showLoading) setNotificationsLoading(false);
    }
  }, [
    notifications,
    notificationsHasMore,
    notificationsOlderLoading,
    setNotifications,
    setUnreadNotificationCount,
    setNotificationsHasMore,
    setNotificationsLoading,
    setNotificationsOlderLoading,
    setNotificationsError,
    setNotificationsOlderError,
    userId,
  ]);

  const loadUnreadNotificationCount = useCallback(async () => {
    if (!supabase || !userId) {
      setUnreadNotificationCount(0);
      return;
    }
    try {
      const { count, error } = await supabase.from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .is('read_at', null);
      if (error) throw error;
      setUnreadNotificationCount(count || 0);
    } catch (error) {
      setNotificationsError(`Could not load unread notification count: ${error.message || 'Network request failed.'}`);
    }
  }, [setUnreadNotificationCount, setNotificationsError, userId]);

  const markNotificationRead = useCallback(async notification => {
    if (notification.read_at) return true;
    if (notificationBusyId === notification.id || notificationBulkBusy) return false;
    setNotificationBusyId(notification.id);
    let result;
    try {
      result = await supabase.from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notification.id)
        .eq('recipient_id', userId)
        .is('read_at', null)
        .select('id,read_at')
        .maybeSingle();
    } finally {
      setNotificationBusyId(null);
    }
    const { data, error } = result;
    if (error) {
      setNotificationsError(`Could not mark notification as read: ${error.message}`);
      return false;
    }
    if (!data) {
      setNotificationsError('Notification was not updated. Refresh the inbox and try again.');
      return false;
    }
    setNotifications(current => current.map(item => (
      item.id === notification.id ? { ...item, read_at: data.read_at } : item
    )));
    setUnreadNotificationCount(count => Math.max(0, count - 1));
    return true;
  }, [
    notificationBusyId,
    notificationBulkBusy,
    setNotificationBusyId,
    setNotificationsError,
    setNotifications,
    setUnreadNotificationCount,
    userId,
  ]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!supabase || !userId || notificationBulkBusy || notificationBusyId || unreadNotificationCount === 0) return;
    setNotificationBulkBusy(true);
    setNotificationsError('');
    const readAt = new Date().toISOString();
    let updated = false;
    try {
      const { error } = await supabase.from('notifications')
        .update({ read_at: readAt })
        .eq('recipient_id', userId)
        .is('read_at', null);
      if (error) throw error;
      updated = true;
      setNotifications(current => current.map(item => item.read_at ? item : { ...item, read_at: readAt }));
      setUnreadNotificationCount(0);
      await loadNotifications(false);
    } catch (error) {
      setNotificationsError(
        `${updated ? 'Could not refresh notifications' : 'Could not mark all notifications as read'}: ${error.message || 'Network request failed.'}`,
      );
    } finally {
      setNotificationBulkBusy(false);
    }
  }, [
    notificationBulkBusy,
    notificationBusyId,
    unreadNotificationCount,
    setNotificationBulkBusy,
    setNotificationsError,
    setNotifications,
    setUnreadNotificationCount,
    loadNotifications,
    userId,
  ]);

  loadNotificationsRef.current = loadNotifications;
  useEffect(() => {
    loadedFor.current = '';
    setNotifications([]);
    setNotificationsHasMore(false);
    setNotificationsError('');
    setNotificationsOlderError('');
    loadUnreadNotificationCount();
  }, [
    userId,
    loadUnreadNotificationCount,
    setNotifications,
    setNotificationsHasMore,
    setNotificationsError,
    setNotificationsOlderError,
  ]);
  useEffect(() => {
    if (tab !== 'notifications' || loadedFor.current === userId) return;
    loadedFor.current = userId;
    loadNotifications();
  }, [userId, tab, loadNotifications]);
  useEffect(() => {
    if (!supabase || !userId) return undefined;
    const channel = supabase.channel('notifications-live')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `recipient_id=eq.${userId}`,
      }, () => {
        if (activeTab.current === 'notifications') loadNotificationsRef.current?.(false);
        else loadUnreadNotificationCount();
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [userId, loadUnreadNotificationCount]);

  return {
    loadNotifications,
    markNotificationRead,
    markAllNotificationsRead,
  };
}
